import { useState } from "react";
import { Alert, App } from "antd";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSelector } from "react-redux";

import { handleErrors } from "@/shared/utils/forms";
import {
  useDeleteOrganizationByIdMutation,
  usePatchOrganizationByIdMutation,
} from "@/redux/service";
import { Roles } from "@/shared/utils/constants";
import CancelConfirmationModal from "@/components_v2/Modal/CancelConfirmationModal";
import FormFieldWrapper from "@/components_v2/Wrapper/FormFieldWrapper";
import { HookFormInput } from "@/components_v2/FormFields/ReactHookForm/fields";
import { useOrganizationPermissions } from "@/features/organizations/hooks/useOrganizationPermissions";
import { SettingsProps } from "@/features/organizations/profile/types";
import BaseButton from "@/components_v2/Button/BaseButton";
import BaseSvgRenderer from "@/components_v2/Svg/BaseSvgRenderer";
import ExportIcon from "@/assets/icons/export.svg?react";
import DeleteIcon from "@/assets/icons/delete.svg?react";
import DatabaseIcon from "@/assets/icons/database.svg?react";
import WarningIcon from "@/assets/icons/warning.svg?react";
import GlobeIcon from "@/assets/icons/globe.svg?react";
import DomainTable from "@/features/organizations/profile/components/DomainTable";
import { useOrganizationExportDownload } from "@/features/organizations/profile/hooks/useOrganizationExportDownload";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import { selectCurrentUser } from "@/redux/authentication/selectors";

const Settings = ({ organization }: SettingsProps) => {
  const { notification } = App.useApp();
  const { t } = useTranslation();

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteOrganizationById] = useDeleteOrganizationByIdMutation();
  const [patchOrganizationById, { isLoading: patchOrganizationLoading }] =
    usePatchOrganizationByIdMutation();
  const currentUser = useSelector(selectCurrentUser);
  const { canDelete, canManageDomains, canExportData } =
    useOrganizationPermissions(currentUser, organization);
  const { startDownload, isDownloading } = useOrganizationExportDownload();

  const deleteOrganizationSchema = yup.object().shape({
    password: yup
      .string()
      .typeError(t("form.validation.notValid"))
      .min(8, t("form.validation.notValid"))
      .matches(/[A-Z]/, t("form.validation.notValid"))
      .matches(/[0-9]/, t("form.validation.notValid"))
      .required(t("form.validation.required")),
  });

  const form = useForm({
    resolver: yupResolver(deleteOrganizationSchema),
    mode: "onChange",
  });

  const handleDelete = async () => {
    if (!currentUser) return;

    if (currentUser.role === Roles.ADMIN.value) {
      deleteOrganizationById({ id: organization.id })
        .unwrap()
        .then(() => {
          notification.success({
            message: t("organizations.labels.organizationDeleted"),
          });
          setOpenDeleteModal(false);
        })
        .catch((errors) => handleErrors(errors, form.setError, notification));
    } else {
      const isValid = await form.trigger();
      if (isValid) {
        deleteOrganizationById({ id: organization.id, data: form.getValues() })
          .unwrap()
          .then((response) => {
            if (response?.code) {
              notification.success({
                message: t(`organizations.labels.${response.code}`),
              });
            }
            setOpenDeleteModal(false);
          })
          .catch((errors) => handleErrors(errors, form.setError, notification));
      }
    }
  };

  const handleStopDeletion = () => {
    patchOrganizationById({ id: organization.id, data: { deletionDate: "" } })
      .unwrap()
      .then(() =>
        notification.success({
          message: t("organizations.labels.deletionCancelled"),
        }),
      );
  };

  return (
    <div className="page-layout">
      {canExportData ? (
        <div className="settings-card flex flex-col gap-200">
          <div className="flex gap-200 items-center">
            <BaseSvgRenderer component={DatabaseIcon} width="20" height="20" />
            <h3 className="header-m-medium">
              {t("organizations.labels.retrieveData")}
            </h3>
          </div>

          <p className="body-m-regular">
            {t("organizations.labels.retrieveDataDescription")}
          </p>

          <BaseButton
            onClick={() => startDownload(organization.id, organization.name)}
            disabled={isDownloading(organization.id)}
            leadingIcon={
              isDownloading(organization.id) ? (
                <BaseSpinner size="small" />
              ) : (
                <BaseSvgRenderer
                  component={ExportIcon}
                  width="20"
                  height="20"
                  stroke="white"
                />
              )
            }
          >
            {t("buttons.export")}
          </BaseButton>
        </div>
      ) : null}

      {canManageDomains ? (
        <div className="settings-card flex flex-col gap-200">
          <div className="flex gap-200 items-center">
            <BaseSvgRenderer component={GlobeIcon} width="20" height="20" />
            <h3 className="header-m-medium">
              {t("organizations.labels.domains")}
            </h3>
          </div>

          <DomainTable
            columnNames={["domain", "createdAt", "actions"]}
            defaultFilters={{ organization: organization.id }}
            description={t("organizations.labels.domainsDescription")}
          />
        </div>
      ) : null}

      {canDelete && currentUser ? (
        <div className="settings-card flex flex-col gap-200">
          <div className="flex gap-200 items-center">
            <BaseSvgRenderer component={WarningIcon} width="20" height="20" />
            <h3 className="header-m-medium">
              {t("organizations.labels.deleteOrganization")}
            </h3>
          </div>

          <p className="body-m-regular">
            {t("organizations.labels.deleteOrganizationDescription")}
          </p>

          {organization.deletionDate ? (
            <div className="flex flex-col gap-200">
              <Alert
                showIcon
                message={t("organizations.labels.deletionTime", {
                  date: DateTime.fromISO(organization.deletionDate).toFormat(
                    "dd MMMM yyyy",
                  ),
                })}
                type="warning"
              />
              <BaseButton
                onClick={handleStopDeletion}
                disabled={patchOrganizationLoading}
              >
                {t("buttons.stopDeletion")}
              </BaseButton>
            </div>
          ) : (
            <>
              <BaseButton
                variant="danger"
                leadingIcon={
                  <BaseSvgRenderer
                    component={DeleteIcon}
                    width="20"
                    height="20"
                    stroke="white"
                  />
                }
                onClick={() => setOpenDeleteModal(true)}
              >
                {t("organizations.labels.delete")}
              </BaseButton>

              <CancelConfirmationModal
                open={openDeleteModal}
                onCancel={() => setOpenDeleteModal(false)}
                onConfirm={handleDelete}
                title={t("organizations.labels.confirmOrganizationDelete")}
                confirmButtonText={t("organizations.labels.delete")}
                content={
                  currentUser.role === Roles.ADMIN.value ? (
                    t(
                      "organizations.labels.deleteOrganizationConfirmationContent1",
                    )
                  ) : (
                    <div>
                      <div>
                        {t(
                          "organizations.labels.deleteOrganizationConfirmationContent1",
                        )}
                      </div>

                      <div>
                        {t(
                          "organizations.labels.deleteOrganizationConfirmationContent2",
                        )}
                      </div>
                      <div>
                        {t(
                          "organizations.labels.deleteOrganizationConfirmationContent3",
                        )}
                      </div>
                      <div>
                        {t(
                          "organizations.labels.deleteOrganizationConfirmationContent4",
                        )}
                      </div>
                      <div>
                        {t(
                          "organizations.labels.deleteOrganizationConfirmationContent5",
                        )}
                      </div>
                      <div>
                        <strong>
                          {t(
                            "organizations.labels.deleteOrganizationConfirmationContent6",
                          )}
                        </strong>
                      </div>
                      <FormFieldWrapper
                        label={t("organizations.labels.password")}
                        required
                      >
                        <HookFormInput
                          name="password"
                          control={form.control}
                          type="password"
                        />
                      </FormFieldWrapper>
                    </div>
                  )
                }
              />
            </>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default Settings;
